import type {ConnectionEditComponentProps} from '@cohort/merchants/apps';
import Button from '@cohort/merchants/components/buttons/Button';
import Input from '@cohort/merchants/components/form/input/Input';
import Header from '@cohort/merchants/components/Header';
import {connectionsKeys} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {getOauthAuthorizationUrl} from '@cohort/merchants/lib/api/Connections';
import type {OAuthErrorMessage, OAuthSuccessMessage} from '@cohort/merchants/lib/Messages';
import {useMessageListener} from '@cohort/merchants/lib/Messages';
import {getOauthRedirectUrl} from '@cohort/merchants/lib/Utils';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import {
  SHOPIFY_MERCHANT_CONNECTOR_ID,
  ShopifyShopIdSchema,
} from '@cohort/shared/schema/apps/shopify/merchantConnector';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import {useCallback, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

function generateOauthPopupFeatures(): string {
  const props = {
    popup: 'yes',
    toolbar: 'no',
    menubar: 'no',
    width: 600,
    height: 700,
  };
  const {width, height} = props;
  const {outerHeight, outerWidth} = window;
  const top = Math.round(outerHeight / 2 - height / 2);
  const left = Math.round(outerWidth / 2 - width / 2);
  return Object.entries({top, left, ...props})
    .map(([k, v]) => `${k}=${v}`)
    .join(',');
}

const SHOPIFY_SHOP_DOMAIN = '.myshopify.com';

const ShopifyConnectionSchema = z.object({
  shopId: z.preprocess(arg => `${arg}${SHOPIFY_SHOP_DOMAIN}`, ShopifyShopIdSchema),
});
type ShopifyConnectionType = z.infer<typeof ShopifyConnectionSchema>;

const ShopIdInputAddon: React.FC = () => (
  <div className="pointer-events-none absolute inset-y-0 right-0 my-px mr-px flex items-center rounded-br-md rounded-tr-md border-l bg-slate-50 px-3 text-sm text-slate-500">
    {SHOPIFY_SHOP_DOMAIN}
  </div>
);

const ShopifyEditConnection: React.FC<ConnectionEditComponentProps> = ({onClose}) => {
  const merchant = useCurrentMerchant();
  const queryClient = useQueryClient();
  const {t} = useTranslation('app-shopify', {
    keyPrefix: 'editConnection',
  });
  const {register, handleSubmit, control} = useForm<ShopifyConnectionType>({
    resolver: zodResolver(ShopifyConnectionSchema),
  });
  const [isLoading, setIsLoading] = useState(false);

  const startOauthFlow = useCallback(
    async (shopId: string) => {
      try {
        const response = await getOauthAuthorizationUrl(merchant.id, {
          connectorId: SHOPIFY_MERCHANT_CONNECTOR_ID,
          adminRedirectUrl: getOauthRedirectUrl(),
          context: {
            shopId,
          },
        });
        window.open(response.authorizationUrl, '_blank', generateOauthPopupFeatures());
      } catch (err) {
        // TODO: catch chop already exists error
        setIsLoading(false);
        notifyError(err);
        onClose();
      }
    },
    [merchant.id, onClose]
  );

  const onMessageReceived = useCallback(
    (message: OAuthSuccessMessage | OAuthErrorMessage) => {
      switch (message.code) {
        case 'oauth-success':
          queryClient.invalidateQueries(connectionsKeys.list(merchant.id));
          onClose();
          break;
        case 'oauth-error':
          onClose();
          break;
      }
      setIsLoading(false);
    },
    [merchant.id, onClose, queryClient]
  );

  useMessageListener(['oauth-success', 'oauth-error'], onMessageReceived);

  return (
    <form
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(async data => {
          setIsLoading(true);
          await startOauthFlow(data.shopId);
        })(e);
      }}
      className="space-y-6 p-6"
      data-testid="start-oauth2-form"
      id="start-oauth2-form"
    >
      <Header title={t('title')} subtitle={t('subtitle')} />
      <Input
        insetPrefix={{
          component: <ShopIdInputAddon />,
          size: 5,
        }}
        type="text"
        name="shopId"
        label={t('labelShopId')}
        register={register}
        control={control}
        description={t('descriptionShopId')}
      />
      <Button
        data-testid="start-oauth2-submit-btn"
        type="submit"
        form="start-oauth2-form"
        loading={isLoading}
      >
        {t('buttonSubmit')}
      </Button>
    </form>
  );
};

export default ShopifyEditConnection;
